/**
 * Helpdesk Phase Configuration
 * 
 * Single source of truth for helpdesk ticket status/phase column definitions.
 * This file defines the visual appearance and properties of each phase column
 * in the helpdesk Kanban board.
 */

import { TicketInternalStatus } from 'shared/types/helpdesk';

export interface HelpdeskPhaseColumn {
  id: TicketInternalStatus;
  label: string;
  color: string;
  bgColor: string;
  headerBgColor: string;
  borderColor: string;
}

/**
 * Helpdesk Phase Columns
 * Defines all available helpdesk ticket status/phase columns with their styling
 */
export const helpdeskPhaseColumns: HelpdeskPhaseColumn[] = [
  {
    id: TicketInternalStatus.NEW_UNANSWERED,
    label: 'Nieuwe tickets',
    color: 'text-slate-700',
    bgColor: 'bg-slate-50/50',
    headerBgColor: 'bg-slate-50',
    borderColor: 'border-slate-300',
  },
  {
    id: TicketInternalStatus.CONFIRMATION_SENT,
    label: 'Bevestiging van ontvangst verstuurd',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50/50',
    headerBgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
  },
  {
    id: TicketInternalStatus.DAILY_MEETING_TOPICS,
    label: 'Speerpunten dagelijkse helpdeskmeeting',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50/50',
    headerBgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-300',
  },
  {
    id: TicketInternalStatus.ADDITIONAL_INFO_NEEDED,
    label: 'Bijkomende info noodzakelijk na dagelijkse helpdeskmeeting',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50/50',
    headerBgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
  },
  {
    id: TicketInternalStatus.SUPPLIER_TO_CONTACT,
    label: 'Leverancier te contacteren',
    color: 'text-purple-700',
    bgColor: 'bg-purple-50/50',
    headerBgColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
  },
  {
    id: TicketInternalStatus.SUPPLIER_CONTACTED,
    label: 'Leverancier gecontacteerd (voor data)',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-50/50',
    headerBgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-300',
  },
  {
    // Bewust geparkeerd. Grijs, want er gebeurt niets: de kolom mag geen
    // aandacht trekken tussen de fases waar wél werk in zit.
    id: TicketInternalStatus.SNOOZE,
    label: 'Snooze',
    color: 'text-neutral-600',
    bgColor: 'bg-neutral-50/50',
    headerBgColor: 'bg-neutral-100',
    borderColor: 'border-neutral-300',
  },
  {
    id: TicketInternalStatus.EXECUTION_DATE_KNOWN,
    label: 'Datum uitvoering gekend en huurder(s) verwittigd',
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-50/50',
    headerBgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-300',
  },
  {
    id: TicketInternalStatus.WORK_COMPLETED,
    label: 'Werken zijn uitgevoerd',
    color: 'text-teal-700',
    bgColor: 'bg-teal-50/50',
    headerBgColor: 'bg-teal-50',
    borderColor: 'border-teal-300',
  },
  {
    id: TicketInternalStatus.COMPLETED,
    label: 'Voltooid',
    color: 'text-green-700',
    bgColor: 'bg-green-50/50',
    headerBgColor: 'bg-green-50',
    borderColor: 'border-green-300',
  },
];

/**
 * De fases in bordvolgorde.
 *
 * Afgeleid uit `helpdeskPhaseColumns` en niet apart opgesomd: die lijst stond
 * nog twee keer met de hand in `useHelpdeskShared`, en dan is een nieuwe fase
 * toevoegen drie bewerkingen waarvan je er twee vergeet.
 */
export const helpdeskPhaseOrder: TicketInternalStatus[] =
  helpdeskPhaseColumns.map((column) => column.id);

/**
 * Legacy status mapping for backward compatibility
 * Maps old enum values to new ones during migration period
 */
export const mapLegacyStatus = (status: string): TicketInternalStatus => {
  const legacyMap: Record<string, TicketInternalStatus> = {
    'Ontvangen': TicketInternalStatus.NEW_UNANSWERED,
    'Geanalyseerd': TicketInternalStatus.CONFIRMATION_SENT,
    'Gepland': TicketInternalStatus.DAILY_MEETING_TOPICS,
    'Afronding': TicketInternalStatus.WORK_COMPLETED,
    'Gesloten': TicketInternalStatus.COMPLETED,
    'Unknown': TicketInternalStatus.NEW_UNANSWERED,
  };

  return legacyMap[status] || (status as TicketInternalStatus);
};

/**
 * Helper function to get status label from phase columns
 */
export const getStatusLabel = (status: TicketInternalStatus): string => {
  const column = helpdeskPhaseColumns.find(col => col.id === status);
  return column?.label || status;
};

